import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { computeFinancials } from "@/lib/contractCalc";
import { fmtDate } from "@/lib/utils";
import { PageHeader, StatusBadge, KpiTile, currency } from "@/components/Primitives";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, TrendingUp, FileText, DollarSign, Download } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";

const COLORS = ["#1F3C88", "#F7941D", "#2E5C47", "#B07D14", "#992B2B", "#5B6FB0", "#8FA89B", "#C85A32", "#3F7D5B", "#9C6B1E"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const num = (v) => parseFloat(v) || 0;

// Normalize financials from backend (falls back to client calc).
const F = (c) => {
  const f = c.financials || {};
  const cc = computeFinancials(c);
  return {
    gross: f.gross_commission ?? cc.gross,
    lead: f.lead_cost ?? cc.lead,
    agent: f.agent_commission ?? cc.agent,
    broker: f.broker_split ?? cc.broker,
    net: f.company_net ?? cc.companyNet,
  };
};

const closeMonth = (c) => {
  const d = c.close_date || "";
  if (d.includes("/")) return parseInt(d.split("/")[0], 10);
  if (d.includes("-")) return parseInt(d.split("-")[1], 10);
  return 0;
};

const isClosed = (c) => (c.status || "").toLowerCase() === "closed";
const isActive = (c) => (c.status || "").toLowerCase() === "active listing";
const isPending = (c) => (c.status || "").toLowerCase() === "under contract";

const Card = ({ title, action, children, testid }) => (
  <div className="bg-white border border-[#E5E5E2] rounded-sm mb-8" data-testid={testid}>
    {(title || action) && (
      <div className="px-6 py-4 border-b border-[#E5E5E2] flex items-center justify-between gap-3">
        <h3 className="font-serif text-xl text-[#0A0A0A]">{title}</h3>
        {action}
      </div>
    )}
    <div className="p-6">{children}</div>
  </div>
);

const Th = ({ children, right }) => (
  <th className={`px-4 py-3 font-semibold text-xs tracking-[0.1em] uppercase text-[#575755] ${right ? "text-right" : "text-left"}`}>{children}</th>
);
const Td = ({ children, right, bold }) => (
  <td className={`px-4 py-3 ${right ? "text-right tabular-nums" : ""} ${bold ? "font-semibold text-[#0A0A0A]" : "text-[#575755]"}`}>{children}</td>
);

function DataTable({ head, children, testid }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={testid}>
        <thead><tr className="border-b border-[#E5E5E2]">{head}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

const csvMdy = (d) => {
  if (!d) return "";
  const s = String(d).slice(0, 10);
  if (s.includes("/")) { const [m, day, y] = s.split("/"); return `${String(m).padStart(2, "0")}/${String(day).padStart(2, "0")}/${String(y).slice(-2)}`; }
  const [y, m, day] = s.split("-");
  return (y && m && day) ? `${m}/${day}/${String(y).slice(2)}` : s;
};

function CsvButton({ filename, headers, rows, testid }) {
  const onClick = () => {
    const esc = (v) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const csv = [headers, ...rows].map((r) => r.map(esc).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-[#E5E5E2] rounded-sm text-sm font-semibold text-[#1F3C88] hover:bg-[#F8FAFE] transition-colors">
      <Download className="w-4 h-4" /> CSV
    </button>
  );
}

const money0 = (n) => `$${Math.round(n || 0).toLocaleString("en-US")}`;
const dateCmp = (a, b) => { const da = a.close_date || "", db = b.close_date || ""; if (!da && !db) return 0; if (!da) return 1; if (!db) return -1; return da.localeCompare(db); };

const MonthlyVolTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-[#E5E5E2] rounded-sm shadow px-3 py-2 text-xs">
      <div className="font-semibold text-[#0A0A0A] mb-1">{label}</div>
      <div className="text-[#1F3C88] font-semibold">{money0(d.volume)}</div>
      <div className="text-[#575755]">Closed: <span className="font-semibold">{d.closedCount}</span></div>
      <div className="text-[#992B2B]">Cancelled: <span className="font-semibold">{d.cancelledCount}</span></div>
    </div>
  );
};

export default function SalesAnalytics() {
  const { user } = useAuth();
  const canWrite = user.access?.sales ? user.access.sales === "write" : (user.role === "admin" || user.role === "sales_manager");
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);
  const [year, setYear] = useState(currentYear);
  const [contracts, setContracts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get("/contracts", { params: { year } }),
        api.get("/analytics/summary", { params: { year } }),
      ]);
      setContracts(c.data);
      setSummary(s.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    try {
      const res = await api.get("/analytics/report.pdf", { params: { year }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `sales-report-${year}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (err) {
      toast.error("Could not generate PDF report");
    }
  };

  const closed = useMemo(() => contracts.filter(isClosed), [contracts]);
  const active = useMemo(() => contracts.filter(isActive), [contracts]);
  const pending = useMemo(() => contracts.filter(isPending), [contracts]);

  return (
    <div data-testid="sales-analytics-page">
      <PageHeader
        title="Sales Analytics"
        subtitle="Agent, company and vendor performance plus a full expense breakdown for your closed business."
        action={
          <div className="flex items-center gap-3">
            <Link to="/sales" data-testid="back-to-sales" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#1F3C88] text-[#1F3C88] text-sm font-semibold hover:bg-[#1F3C88] hover:text-white transition-colors duration-200">
              <ArrowLeft className="w-4 h-4" /> Tracker
            </Link>
            {canWrite && (
              <button onClick={downloadPdf} data-testid="download-report-pdf" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#1F3C88] text-white text-sm font-semibold hover:bg-[#F7941D] transition-colors duration-200">
                <Download className="w-4 h-4" /> PDF Report
              </button>
            )}
            <select data-testid="analytics-year-filter" value={year} onChange={(e) => setYear(parseInt(e.target.value))}
              className="px-4 py-2.5 bg-white border border-[#E5E5E2] rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]">
              {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        }
      />

      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          <KpiTile index={0} testid="kpi-volume-ytd" color="#16A34A" Icon={TrendingUp} value={summary.volume_count} label="Volume YTD"
            rows={[["Closed & Pending:", currency(summary.volume_total)], ["Total GCI:", currency(summary.volume_gci)]]} />
          <KpiTile index={1} testid="kpi-active-listings" color="#2563EB" Icon={TrendingUp} value={summary.active_count} label="Active Listings"
            rows={[["Volume:", currency(summary.active_volume)], ["Gross GCI:", currency(summary.active_gci)]]} />
          <KpiTile index={2} testid="kpi-pending-contracts" color="#F59E0B" Icon={FileText} value={summary.pending_count} label="Pending Contracts"
            rows={[["Volume:", currency(summary.pending_volume)], ["Gross GCI:", currency(summary.pending_gci)]]} />
          <KpiTile index={3} testid="kpi-closed-transactions" color="#16A34A" Icon={DollarSign} value={summary.closed_count} label="Closed Transactions"
            rows={[["Volume:", currency(summary.closed_volume)], ["Gross GCI:", currency(summary.closed_gci)]]} />
        </div>
      )}

      {loading ? (
        <div className="text-center text-[#8a8a86] py-20" data-testid="analytics-loading">Loading analytics…</div>
      ) : (
        <Tabs defaultValue="agent" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="agent" data-testid="tab-agent">Agent</TabsTrigger>
            <TabsTrigger value="company" data-testid="tab-company">Company</TabsTrigger>
            <TabsTrigger value="vendor" data-testid="tab-vendor">Vendor</TabsTrigger>
            <TabsTrigger value="expenses" data-testid="tab-expenses">Expenses</TabsTrigger>
          </TabsList>

          <TabsContent value="agent"><AgentTab contracts={contracts} closed={closed} active={active} pending={pending} year={year} /></TabsContent>
          <TabsContent value="company"><CompanyTab closed={closed} contracts={contracts} year={year} /></TabsContent>
          <TabsContent value="vendor"><VendorTab closed={closed} /></TabsContent>
          <TabsContent value="expenses"><ExpensesTab closed={closed} year={year} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}

// ── Agent Performance ────────────────────────────────────────────────────────
function AgentTab({ contracts, closed, active, pending, year }) {
  const agents = useMemo(() => Array.from(new Set(contracts.map((c) => c.agent_name).filter(Boolean))).sort(), [contracts]);
  const [agent, setAgent] = useState("__all_no_matt__");
  const [monthFilter, setMonthFilter] = useState("all");
  const [selDeal, setSelDeal] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const isAll = agent === "__all__";
  const isAllNoMatt = agent === "__all_no_matt__";
  const MATT = "Matt Collins";
  const showAgentCol = isAll || isAllNoMatt;
  const scopeLabel = isAll ? "All Agents" : isAllNoMatt ? "All Agents (excl. Matt Collins)" : agent;
  const chartScope = isAll ? "Company" : isAllNoMatt ? "Company (excl. Matt)" : agent;
  const inSel = (c) => isAll ? true : isAllNoMatt ? c.agent_name !== MATT : c.agent_name === agent;
  const match = inSel;

  const leaderboard = useMemo(() => {
    const sel = (c) => agent === "__all__" ? true : agent === "__all_no_matt__" ? c.agent_name !== "Matt Collins" : c.agent_name === agent;
    const by = {};
    const ensure = (n) => (by[n] = by[n] || { agent: n, closedCount: 0, closedVolume: 0, gci: 0, agentComm: 0, net: 0, pendingCount: 0, pendingVolume: 0 });
    closed.filter(sel).forEach((c) => { const b = ensure(c.agent_name || "Unknown"); const f = F(c); b.closedCount++; b.closedVolume += num(c.contract_value); b.gci += f.gross; b.agentComm += f.agent; b.net += f.net; });
    pending.filter(sel).forEach((c) => { const b = ensure(c.agent_name || "Unknown"); b.pendingCount++; b.pendingVolume += num(c.contract_value); });
    return Object.values(by).sort((a, b) => b.closedVolume - a.closedVolume);
  }, [closed, pending, agent]);

  const volumeChart = leaderboard.filter((r) => r.closedVolume > 0).map((r) => ({ name: r.agent, value: Math.round(r.closedVolume) }));

  const monthly = useMemo(() => MONTHS.map((m, i) => {
    const sel = (c) => (agent === "__all__" ? true : agent === "__all_no_matt__" ? c.agent_name !== "Matt Collins" : c.agent_name === agent) && closeMonth(c) === i + 1;
    const mc = closed.filter(sel);
    const net = Math.round(mc.reduce((s, c) => s + F(c).net, 0));
    const pendingProfit = Math.round(pending.filter(sel).reduce((s, c) => s + F(c).net, 0));
    return {
      month: m,
      volume: Math.round(mc.reduce((s, c) => s + num(c.contract_value), 0)),
      net,
      base: Math.min(net, 25000),
      bonus: Math.max(net - 25000, 0),
      pendingProfit,
      closedCount: mc.length,
      cancelledCount: contracts.filter((c) => (c.status || "").toLowerCase() === "cancelled" && sel(c)).length,
    };
  }), [closed, pending, contracts, agent]);

  const agentClosed = closed
    .filter((c) => match(c) && (monthFilter === "all" || closeMonth(c) === parseInt(monthFilter)))
    .sort((a, b) => (sortDir === "asc" ? dateCmp(a, b) : -dateCmp(a, b)));
  const totVol = agentClosed.reduce((s, c) => s + num(c.contract_value), 0);
  const totComm = agentClosed.reduce((s, c) => s + F(c).agent, 0);
  const totNet = agentClosed.reduce((s, c) => s + F(c).net, 0);

  return (
    <div>
      <Card title="Agent Leaderboard" testid="agent-leaderboard-card"
        action={<div className="flex items-center gap-2"><CsvButton testid="agent-leaderboard-csv" filename={`agent-leaderboard-${year}.csv`} headers={["Agent", "Closed", "Volume", "GCI", "Agent Comm", "Company Net", "Pending Count", "Pending Volume"]} rows={leaderboard.map((r) => [r.agent, r.closedCount, Math.round(r.closedVolume), Math.round(r.gci), Math.round(r.agentComm), Math.round(r.net), r.pendingCount, Math.round(r.pendingVolume)])} /><select data-testid="agent-select" value={agent} onChange={(e) => setAgent(e.target.value)} className="px-3 py-2 bg-white border border-[#E5E5E2] rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]"><option value="__all__">All Agents</option><option value="__all_no_matt__">All Agents (excl. Matt Collins)</option>{agents.map((a) => <option key={a} value={a}>{a}</option>)}</select></div>}>
        <DataTable testid="agent-leaderboard-table" head={<><Th>Agent</Th><Th right>Closed</Th><Th right>Volume</Th><Th right>GCI</Th><Th right>Agent Comm</Th><Th right>Company Net</Th><Th right>Pending</Th></>}>
          {leaderboard.length === 0 ? (
            <tr><Td>—</Td><Td right>0</Td><Td right>—</Td><Td right>—</Td><Td right>—</Td><Td right>—</Td><Td right>0</Td></tr>
          ) : leaderboard.map((r) => (
            <tr key={r.agent} className="border-b border-[#F0EFEC] hover:bg-[#FBFAF8]">
              <Td bold>{r.agent}</Td>
              <Td right>{r.closedCount}</Td>
              <Td right>{money0(r.closedVolume)}</Td>
              <Td right>{money0(r.gci)}</Td>
              <Td right>{money0(r.agentComm)}</Td>
              <Td right><span className="font-semibold text-[#1F3C88]">{money0(r.net)}</span></Td>
              <Td right>{r.pendingCount} · {money0(r.pendingVolume)}</Td>
            </tr>
          ))}
        </DataTable>
      </Card>

      {volumeChart.length > 0 && (
        <Card title="Closed Volume by Agent" testid="agent-volume-chart-card">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={volumeChart} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EDEDEA" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={60} />
              <YAxis tickFormatter={money0} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => money0(v)} />
              <Bar dataKey="value" name="Closed Volume" radius={[4, 4, 0, 0]}>
                {volumeChart.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card title={`Monthly Closed Volume — ${chartScope} ${year}`} testid="agent-monthly-volume-card">
          <div className="flex items-center gap-4 text-sm mb-3" data-testid="agent-volume-counts">
            <span className="text-[#575755]">Closed: <span className="font-semibold text-[#1F3C88]">{monthly.reduce((s, m) => s + m.closedCount, 0)}</span></span>
            <span className="text-[#575755]">Cancelled: <span className="font-semibold text-[#992B2B]">{monthly.reduce((s, m) => s + m.cancelledCount, 0)}</span></span>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthly} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EDEDEA" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={money0} tick={{ fontSize: 11 }} />
              <Tooltip content={<MonthlyVolTooltip />} />
              <Bar dataKey="volume" name="Closed Volume" fill="#1F3C88" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title={`Monthly Company Profit — ${chartScope} ${year}`} testid="agent-monthly-profit-card">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthly} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EDEDEA" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={money0} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v, n) => [money0(v), n]} />
              <Legend />
              <Bar dataKey="base" name="Profit (to $25k)" stackId="p" fill="#992B2B" radius={[0, 0, 0, 0]} />
              <Bar dataKey="bonus" name="DOO Bonus (over $25k)" stackId="p" fill="#2E5C47" radius={[4, 4, 0, 0]} />
              <Bar dataKey="pendingProfit" name="Pending Profit (expected)" fill="#93C5FD" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card title={`Closed Contracts — ${scopeLabel} (${agentClosed.length})`} testid="agent-closed-table-card"
        action={
          <div className="flex items-center gap-2">
            <CsvButton testid="agent-closed-csv" filename={`agent-closed-${scopeLabel}-${year}.csv`} headers={["Close Date", "Accepted Date", "Agent", "Client", "Address", "Value", "Agent Comm", "Company Net"]} rows={agentClosed.map((c) => { const f = F(c); return [csvMdy(c.close_date), csvMdy(c.creation_date), c.agent_name, `${c.first_name || ""} ${c.last_name || ""}`.trim(), c.address, Math.round(num(c.contract_value)), Math.round(f.agent), Math.round(f.net)]; })} />
            <button type="button" data-testid="agent-closed-sort" onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
              className="px-3 py-2 bg-white border border-[#E5E5E2] rounded-sm text-sm font-semibold text-[#1F3C88] hover:bg-[#F8FAFE]">
              Close date {sortDir === "asc" ? "↑ Soonest" : "↓ Latest"}
            </button>
            <select data-testid="closed-month-filter" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}
              className="px-3 py-2 bg-white border border-[#E5E5E2] rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]">
              <option value="all">All Months</option>
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </div>
        }>
        <DataTable head={<><Th>Close Date</Th><Th>Accepted Date</Th>{showAgentCol && <Th>Agent</Th>}<Th>Client</Th><Th>Address</Th><Th right>Value</Th><Th right>Agent Comm</Th><Th right>Company Net</Th></>}>
          {agentClosed.length === 0 ? (
            <tr><Td>No closed contracts</Td></tr>
          ) : agentClosed.map((c) => { const f = F(c); return (
            <tr key={c.id} className="border-b border-[#F0EFEC] hover:bg-[#FBFAF8]">
              <Td>{fmtDate(c.close_date)}</Td>
              <Td>{fmtDate(c.creation_date)}</Td>
              {showAgentCol && <Td>{c.agent_name}</Td>}
              <Td>{c.first_name} {c.last_name}</Td>
              <Td>
                <button data-testid={`deal-link-${c.id}`} onClick={() => setSelDeal(c)}
                  className="text-[#1F3C88] hover:text-[#F7941D] hover:underline underline-offset-2 font-medium text-left transition-colors duration-200">
                  {c.address || "View deal"}
                </button>
              </Td>
              <Td right>{money0(num(c.contract_value))}</Td>
              <Td right>{money0(f.agent)}</Td>
              <Td right><span className="font-semibold text-[#1F3C88]">{money0(f.net)}</span></Td>
            </tr>
          ); })}
          {agentClosed.length > 0 && (
            <tr className="bg-[#F3F6F4] font-semibold border-t-2 border-[#E5E5E2]">
              <Td bold>Summary</Td><Td />{showAgentCol && <Td />}<Td bold>{agentClosed.length} deals</Td><Td />
              <Td right>{money0(totVol)}</Td><Td right>{money0(totComm)}</Td><Td right><span className="text-[#1F3C88]">{money0(totNet)}</span></Td>
            </tr>
          )}
        </DataTable>
      </Card>

      <DealModal deal={selDeal} onClose={() => setSelDeal(null)} />
    </div>
  );
}

const statusTone = (s) => ({ closed: "green", "under contract": "amber", "active listing": "orange", cancelled: "red" }[(s || "").toLowerCase()] || "gray");

function DealModal({ deal, onClose }) {
  if (!deal) return null;
  const f = F(deal);
  const Row = ({ label, value, accent }) => (
    <div className="flex items-center justify-between py-1.5 text-sm border-b border-[#F0EFEC] last:border-0">
      <span className="text-[#575755]">{label}</span>
      <span className={`tabular-nums ${accent ? "font-semibold text-[#1F3C88]" : "text-[#0A0A0A]"}`}>{value}</span>
    </div>
  );
  const fees = [
    ["Lead Cost", f.lead], ["Agent Commission", f.agent], ["Broker Split", f.broker],
    ["Risk Mgmt Fee", num(deal.risk_management_fee)], ["Cap Fee (100%)", num(deal.cap_fee_100)],
    ["Transaction Review Fee", num(deal.transaction_review_fee)], ["TC Fee", num(deal.transaction_coordinator_fee)],
    ["Referral Fee", num(deal.referral_fee)],
  ];
  return (
    <Dialog open={!!deal} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl bg-white max-h-[85vh] overflow-y-auto" data-testid="deal-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{deal.address || "Deal"}</DialogTitle>
          <DialogDescription>{[deal.city, deal.state, deal.zip_code].filter(Boolean).join(", ") || "Read-only deal summary"}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <StatusBadge tone={statusTone(deal.status)}>{deal.status}</StatusBadge>
          <span className="text-xs text-[#a3a099] uppercase tracking-wide">{deal.transaction_type}</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 mt-2">
          <div>
            <div className="text-xs tracking-[0.14em] uppercase font-semibold text-[#F7941D] mb-1 mt-2">Details</div>
            <Row label="Agent" value={deal.agent_name || "—"} />
            <Row label="Client" value={`${deal.first_name || ""} ${deal.last_name || ""}`.trim() || "—"} />
            <Row label="Close Date" value={fmtDate(deal.close_date)} />
            <Row label="Lead Type" value={deal.lead_type || "—"} />
            <Row label="Lead Gen" value={deal.lead_generated_by || "—"} />
            <Row label="Lender" value={deal.lender || "—"} />
            <Row label="Title Co." value={deal.title_company || "—"} />
            <Row label="Contract Value" value={money0(num(deal.contract_value))} />
            <Row label="Commission Rate" value={`${deal.commission_rate || 0}%`} />
          </div>
          <div>
            <div className="text-xs tracking-[0.14em] uppercase font-semibold text-[#F7941D] mb-1 mt-2">Commission Audit</div>
            <Row label="Gross Commission" value={money0(f.gross)} />
            {fees.map(([l, v]) => <Row key={l} label={`− ${l}`} value={v ? `(${money0(v)})` : money0(0)} />)}
            <Row label="Company Net" value={money0(f.net)} accent />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function YoYComparison({ year }) {
  const prev = year - 1;
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/contracts", { params: { year } }),
      api.get("/contracts", { params: { year: prev } }),
    ]).then(([cur, old]) => {
      if (!alive) return;
      const build = (rows) => {
        const cl = rows.data.filter(isClosed);
        const monthly = MONTHS.map((m, i) => Math.round(cl.filter((c) => closeMonth(c) === i + 1).reduce((s, c) => s + num(c.contract_value), 0)));
        return { count: cl.length, volume: cl.reduce((s, c) => s + num(c.contract_value), 0), net: cl.reduce((s, c) => s + F(c).net, 0), monthly };
      };
      setData({ cur: build(cur), old: build(old) });
    }).catch(() => {});
    return () => { alive = false; };
  }, [year, prev]);
  if (!data) return null;
  const chart = MONTHS.map((m, i) => ({ month: m, [String(prev)]: data.old.monthly[i], [String(year)]: data.cur.monthly[i] }));
  const delta = (a, b) => (b === 0 ? (a > 0 ? 100 : 0) : ((a - b) / b) * 100);
  const Row = ({ label, cur, old, money }) => {
    const d = delta(cur, old);
    return (
      <div className="flex items-center justify-between py-2 border-b border-[#F0EFEC] text-sm">
        <span className="text-[#575755]">{label}</span>
        <div className="flex items-center gap-4 tabular-nums">
          <span className="text-[#a3a099] w-28 text-right">{money ? money0(old) : old}</span>
          <span className="font-semibold text-[#1F3C88] w-28 text-right">{money ? money0(cur) : cur}</span>
          <span className={`w-16 text-right font-semibold ${d >= 0 ? "text-[#2E5C47]" : "text-[#992B2B]"}`}>{d >= 0 ? "+" : ""}{d.toFixed(0)}%</span>
        </div>
      </div>
    );
  };
  return (
    <Card title={`Year over Year — ${prev} vs ${year}`} testid="company-yoy-card">
      <div className="flex items-center justify-end gap-4 text-[11px] uppercase tracking-wide text-[#a3a099] mb-1">
        <span className="w-28 text-right">{prev}</span><span className="w-28 text-right">{year}</span><span className="w-16 text-right">Δ</span>
      </div>
      <Row label="Closed Transactions" cur={data.cur.count} old={data.old.count} />
      <Row label="Closed Volume" cur={data.cur.volume} old={data.old.volume} money />
      <Row label="Company Profit" cur={data.cur.net} old={data.old.net} money />
      <div className="mt-6">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chart} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EDEDEA" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={money0} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => money0(v)} />
            <Legend />
            <Bar dataKey={String(prev)} name={String(prev)} fill="#B9B5AC" radius={[4, 4, 0, 0]} />
            <Bar dataKey={String(year)} name={String(year)} fill="#1F3C88" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

// ── Company Performance ──────────────────────────────────────────────────────
function CompanyTab({ closed, contracts, year }) {
  const [monthFilter, setMonthFilter] = useState("all");
  const [selDeal, setSelDeal] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const monthly = MONTHS.map((m, i) => {
    const mc = closed.filter((c) => closeMonth(c) === i + 1);
    return {
      month: m,
      volume: Math.round(mc.reduce((s, c) => s + num(c.contract_value), 0)),
      net: Math.round(mc.reduce((s, c) => s + F(c).net, 0)),
      count: mc.length,
      closedCount: mc.length,
      cancelledCount: (contracts || []).filter((c) => (c.status || "").toLowerCase() === "cancelled" && closeMonth(c) === i + 1).length,
    };
  });

  const pie = (key) => {
    const acc = {};
    closed.filter((c) => (c.transaction_type || "").toLowerCase() === "purchase").forEach((c) => {
      const k = c[key] || "Not Specified"; acc[k] = (acc[k] || 0) + 1;
    });
    return Object.entries(acc).map(([name, value]) => ({ name, value }));
  };
  const lenders = pie("lender");
  const titles = pie("title_company");
  const leads = pie("lead_type");

  const filteredClosed = closed
    .filter((c) => monthFilter === "all" || closeMonth(c) === parseInt(monthFilter))
    .sort((a, b) => (sortDir === "asc" ? dateCmp(a, b) : -dateCmp(a, b)));
  const fVol = filteredClosed.reduce((s, c) => s + num(c.contract_value), 0);
  const fNet = filteredClosed.reduce((s, c) => s + F(c).net, 0);

  return (
    <div>
      <YoYComparison year={year} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card title={`Monthly Company Volume — ${year}`} testid="company-monthly-volume-card">
          <div className="flex items-center gap-4 text-sm mb-3" data-testid="company-volume-counts">
            <span className="text-[#575755]">Closed: <span className="font-semibold text-[#1F3C88]">{monthly.reduce((s, m) => s + m.closedCount, 0)}</span></span>
            <span className="text-[#575755]">Cancelled: <span className="font-semibold text-[#992B2B]">{monthly.reduce((s, m) => s + m.cancelledCount, 0)}</span></span>
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={monthly} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EDEDEA" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={money0} tick={{ fontSize: 11 }} />
              <Tooltip content={<MonthlyVolTooltip />} />
              <Bar dataKey="volume" name="Closed Volume" fill="#1F3C88" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title={`Monthly Company Profit — ${year}`} testid="company-monthly-profit-card">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={monthly} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EDEDEA" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={money0} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => money0(v)} />
              <Bar dataKey="net" name="Company Profit" fill="#2E5C47" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {[["Lenders Used", lenders], ["Title Companies", titles], ["Lead Sources", leads]].map(([label, data]) => (
          <Card key={label} title={label} testid={`company-pie-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            {data.length === 0 ? <p className="text-center text-sm text-[#8a8a86] py-10">No closed purchases</p> : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={data} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`} labelLine={false} style={{ fontSize: 10 }}>
                    {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        ))}
      </div>

      <Card title={`Closed Contracts by Month (${filteredClosed.length})`} testid="company-by-month-card"
        action={
          <div className="flex items-center gap-2">
            <CsvButton testid="company-closed-csv" filename={`company-closed-${year}.csv`} headers={["Date", "Agent", "Client", "Property", "Value", "Company Net"]} rows={filteredClosed.map((c) => [csvMdy(c.close_date), c.agent_name, `${c.first_name || ""} ${c.last_name || ""}`.trim(), c.address, Math.round(num(c.contract_value)), Math.round(F(c).net)])} />
            <button type="button" data-testid="company-closed-sort" onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
              className="px-3 py-2 bg-white border border-[#E5E5E2] rounded-sm text-sm font-semibold text-[#1F3C88] hover:bg-[#F8FAFE]">
              Close date {sortDir === "asc" ? "↑ Soonest" : "↓ Latest"}
            </button>
            <select data-testid="company-month-filter" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}
              className="px-3 py-2 bg-white border border-[#E5E5E2] rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]">
              <option value="all">All Months</option>
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </div>
        }>
        <DataTable head={<><Th>Date</Th><Th>Agent</Th><Th>Client</Th><Th>Property</Th><Th right>Value</Th><Th right>Company Net</Th></>}>
          {filteredClosed.length === 0 ? <tr><Td>No closed contracts</Td></tr> : filteredClosed.map((c) => (
            <tr key={c.id} className="border-b border-[#F0EFEC] hover:bg-[#FBFAF8]">
              <Td>{fmtDate(c.close_date)}</Td>
              <Td>{c.agent_name}</Td>
              <Td>{c.first_name} {c.last_name}</Td>
              <Td>
                <button data-testid={`deal-link-${c.id}`} onClick={() => setSelDeal(c)}
                  className="text-[#1F3C88] hover:text-[#F7941D] hover:underline underline-offset-2 font-medium text-left transition-colors duration-200">
                  {c.address || "View deal"}
                </button>
              </Td>
              <Td right>{money0(num(c.contract_value))}</Td>
              <Td right><span className="font-semibold text-[#1F3C88]">{money0(F(c).net)}</span></Td>
            </tr>
          ))}
          {filteredClosed.length > 0 && (
            <tr className="bg-[#F3F6F4] font-semibold border-t-2 border-[#E5E5E2]">
              <Td bold>Total</Td><Td /><Td bold>{filteredClosed.length} deals</Td><Td />
              <Td right>{money0(fVol)}</Td><Td right><span className="text-[#1F3C88]">{money0(fNet)}</span></Td>
            </tr>
          )}
        </DataTable>
      </Card>

      <DealModal deal={selDeal} onClose={() => setSelDeal(null)} />
    </div>
  );
}

// ── Vendor Performance ───────────────────────────────────────────────────────
function VendorTab({ closed }) {
  const build = (key) => {
    const acc = {};
    closed.forEach((c) => {
      const k = c[key] || "Not Specified";
      acc[k] = acc[k] || { name: k, count: 0, volume: 0 };
      acc[k].count++; acc[k].volume += num(c.contract_value);
    });
    return Object.values(acc).sort((a, b) => b.count - a.count || b.volume - a.volume);
  };
  const lenders = build("lender");
  const titles = build("title_company");

  const Section = ({ label, rows, testid }) => (
    <Card title={label} testid={testid} action={<CsvButton testid={`${testid}-csv`} filename={`${label}.csv`} headers={[label, "Deals", "Volume"]} rows={rows.map((r) => [r.name, r.count, Math.round(r.volume)])} />}>
      {rows.length === 0 ? <p className="text-center text-sm text-[#8a8a86] py-8">No closed contracts</p> : (
        <>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={rows.map((r) => ({ name: r.name, value: Math.round(r.volume) }))} margin={{ top: 8, right: 16, left: 8, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EDEDEA" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={50} />
              <YAxis tickFormatter={money0} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => money0(v)} />
              <Bar dataKey="value" name="Closed Volume" radius={[4, 4, 0, 0]}>{rows.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4">
            <DataTable head={<><Th>{label}</Th><Th right>Deals</Th><Th right>Volume</Th></>}>
              {rows.map((r) => (
                <tr key={r.name} className="border-b border-[#F0EFEC] hover:bg-[#FBFAF8]"><Td bold>{r.name}</Td><Td right>{r.count}</Td><Td right>{money0(r.volume)}</Td></tr>
              ))}
            </DataTable>
          </div>
        </>
      )}
    </Card>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <Section label="Lenders" rows={lenders} testid="vendor-lenders-card" />
      <Section label="Title Companies" rows={titles} testid="vendor-titles-card" />
    </div>
  );
}

// ── Company Expenses ─────────────────────────────────────────────────────────
function ExpensesTab({ closed, year }) {
  const totals = closed.reduce((acc, c) => {
    const f = F(c);
    acc.leadCost += f.lead;
    acc.brokerSplit += f.broker;
    acc.riskMgmt += num(c.risk_management_fee);
    acc.capFee += num(c.cap_fee_100);
    acc.reviewFee += num(c.transaction_review_fee);
    acc.tcFee += num(c.transaction_coordinator_fee);
    acc.referral += num(c.referral_fee);
    return acc;
  }, { leadCost: 0, brokerSplit: 0, riskMgmt: 0, capFee: 0, reviewFee: 0, tcFee: 0, referral: 0 });
  const grand = Object.values(totals).reduce((s, v) => s + v, 0);
  const rows = [
    ["Lead Cost", totals.leadCost], ["Risk Management Fees", totals.riskMgmt], ["100% Cap Fees", totals.capFee],
    ["Transaction Review Fees", totals.reviewFee], ["Broker Split", totals.brokerSplit],
    ["Transaction Coordinator Fees", totals.tcFee], ["Referral Fees", totals.referral],
  ];
  const pct = (v) => (grand > 0 ? ((v / grand) * 100).toFixed(1) : "0.0");

  const tierLabel = (v) => v >= 300000 ? "$300k+" : v >= 225000 ? "$225k – $299k" : v >= 150000 ? "$150k – $224k" : v >= 75000 ? "$75k – $149k" : "Under $75k";
  const tierRate = (v) => v >= 300000 ? 40 : v >= 225000 ? 35 : v >= 150000 ? 30 : v >= 75000 ? 25 : 15;
  const tierOrder = ["$300k+", "$225k – $299k", "$150k – $224k", "$75k – $149k", "Under $75k"];
  const byTier = {};
  const byLeadType = {};
  closed.forEach((c) => {
    const f = F(c);
    if (f.lead > 0 && c.lead_generated_by === "Company Generated") {
      const t = tierLabel(num(c.contract_value));
      byTier[t] = byTier[t] || { count: 0, cost: 0, rate: tierRate(num(c.contract_value)) };
      byTier[t].count++; byTier[t].cost += f.lead;
    }
    if (f.lead > 0) {
      const lt = c.lead_type || "Not Specified";
      byLeadType[lt] = byLeadType[lt] || { count: 0, cost: 0 };
      byLeadType[lt].count++; byLeadType[lt].cost += f.lead;
    }
  });

  const zips = {};
  closed.forEach((c) => { const z = c.zip_code || "Unknown"; zips[z] = zips[z] || { count: 0, vol: 0 }; zips[z].count++; zips[z].vol += num(c.contract_value); });
  const topZips = Object.entries(zips).map(([zip, d]) => ({ zip, count: d.count, avg: d.vol / d.count })).sort((a, b) => b.count - a.count).slice(0, 20);

  return (
    <div>
      <Card title="All Expenses Summary" testid="expenses-summary-card" action={<CsvButton testid="expenses-summary-csv" filename={`expenses-summary-${year}.csv`} headers={["Expense Category", "Total", "% of Total"]} rows={[...rows.map(([label, v]) => [label, Math.round(v), pct(v)]), ["Total Expenses", Math.round(grand), "100"]]} />}>
        <DataTable head={<><Th>Expense Category</Th><Th right>Total</Th><Th right>% of Total</Th></>}>
          {rows.map(([label, v]) => (
            <tr key={label} className="border-b border-[#F0EFEC]"><Td>{label}</Td><Td right>{money0(v)}</Td><Td right>{pct(v)}%</Td></tr>
          ))}
          <tr className="bg-[#F3F6F4] font-semibold border-t-2 border-[#E5E5E2]"><Td bold>Total Expenses</Td><Td right><span className="text-[#992B2B]">{money0(grand)}</span></Td><Td right>100%</Td></tr>
        </DataTable>
      </Card>

      <Card title="Company-Generated Lead Cost by Tier" testid="expenses-tier-card" action={<CsvButton testid="expenses-tier-csv" filename={`expenses-by-tier-${year}.csv`} headers={["Price Tier", "Count", "Rate", "Total Cost"]} rows={tierOrder.filter((t) => byTier[t]).map((t) => [t, byTier[t].count, byTier[t].rate, Math.round(byTier[t].cost)])} />}>
        <DataTable head={<><Th>Price Tier</Th><Th right>Count</Th><Th right>Rate</Th><Th right>Total Cost</Th></>}>
          {Object.keys(byTier).length === 0 ? <tr><Td>No company-generated leads for {year}</Td></tr> :
            tierOrder.filter((t) => byTier[t]).map((t) => (
              <tr key={t} className="border-b border-[#F0EFEC]"><Td bold>{t}</Td><Td right>{byTier[t].count}</Td><Td right>{byTier[t].rate}%</Td><Td right>{money0(byTier[t].cost)}</Td></tr>
            ))}
        </DataTable>
      </Card>

      <Card title="Lead Cost by Lead Type (Source)" testid="expenses-leadtype-card" action={<CsvButton testid="expenses-leadtype-csv" filename={`expenses-by-lead-type-${year}.csv`} headers={["Lead Type", "Contracts", "Total Cost"]} rows={Object.entries(byLeadType).sort((a, b) => b[1].cost - a[1].cost).map(([lt, d]) => [lt, d.count, Math.round(d.cost)])} />}>
        <DataTable head={<><Th>Lead Type</Th><Th right>Contracts</Th><Th right>Total Cost</Th></>}>
          {Object.keys(byLeadType).length === 0 ? <tr><Td>No lead costs for {year}</Td></tr> :
            Object.entries(byLeadType).sort((a, b) => b[1].cost - a[1].cost).map(([lt, d]) => (
              <tr key={lt} className="border-b border-[#F0EFEC]"><Td bold>{lt}</Td><Td right>{d.count}</Td><Td right>{money0(d.cost)}</Td></tr>
            ))}
        </DataTable>
      </Card>

      <Card title={`Top Zip Codes — ${year}`} testid="expenses-zip-card" action={<CsvButton testid="expenses-zip-csv" filename={`top-zips-${year}.csv`} headers={["Zip", "Deals", "Avg Value"]} rows={topZips.map((z) => [z.zip, z.count, Math.round(z.avg)])} />}>
        {topZips.length === 0 ? <p className="text-center text-sm text-[#8a8a86] py-8">No contracts for {year}</p> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {topZips.map((z, i) => (
              <div key={z.zip} className="p-4 border border-[#E5E5E2] rounded-sm bg-[#FBFAF8]">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-[#0A0A0A]">{i + 1}. {z.zip}</span>
                  <span className="text-sm font-semibold text-[#1F3C88]">{z.count} {z.count === 1 ? "deal" : "deals"}</span>
                </div>
                <div className="text-xs text-[#575755] mt-1">Avg value <span className="font-semibold text-[#2E5C47]">{money0(z.avg)}</span></div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
